import translate from "./Translate";

export type LogAction = "edited" | "added" | "deleted";

export type LogItem = {
  key: string;
  value: string | null;
};

export type ParsedLog = {
  authorId: string;
  action: LogAction;
  items: LogItem[];
  raw: string;
};

/**
 * Split a raw block into entries while preserving newlines.
 * A new entry starts at a line beginning with "author:".
 */
const splitLogEntries = (rawText: string): string[] => {
  if (!rawText) return [];
  const allLines = rawText.split(/\r?\n/);

  const entries: string[] = [];
  let currentEntry: string | null = null;

  for (const originalLine of allLines) {
    // Keep the line as-is (including spaces); remove a trailing CR if present.
    const trimmedCR = originalLine.replace(/\r$/, "");

    if (trimmedCR.startsWith("author:")) {
      if (currentEntry !== null) entries.push(currentEntry);
      currentEntry = trimmedCR;
    } else if (currentEntry !== null) {
      currentEntry += "\n" + originalLine;
    } else if (trimmedCR.trim() !== "") {
      // Rare case: the text does not start with author:
      currentEntry = originalLine;
    }
  }

  if (currentEntry !== null) entries.push(currentEntry);
  return entries.filter(Boolean);
};

/**
 * Split a CSV list of values while respecting:
 * - quoted values
 * - escaped quotes (\" or "")
 * - newlines inside quotes
 * The number of splits is limited by expectedKeysCount.
 */
const _splitCsvValuesForKeys = (valuesCsv: string, expectedKeysCount: number): string[] => {
  if (!valuesCsv) return [];
  if (!Number.isFinite(expectedKeysCount) || expectedKeysCount <= 0) return [valuesCsv.trim()];

  const values: string[] = [];
  let currentToken = "";
  let insideQuotes = false;
  let emittedCount = 0;

  for (let charIndex = 0; charIndex < valuesCsv.length; charIndex++) {
    const char = valuesCsv[charIndex];
    const nextChar = valuesCsv[charIndex + 1];

    // Open quotes
    if (char === '"' && !insideQuotes) {
      insideQuotes = true;
      continue;
    }

    // Close / escape quotes
    if (char === '"' && insideQuotes) {
      // "" => literal quote
      if (nextChar === '"') {
        currentToken += '"';
        charIndex++;
        continue;
      }
      // \" => literal quote
      if (valuesCsv[charIndex - 1] === "\\") {
        currentToken = currentToken.slice(0, -1) + '"';
        continue;
      }
      insideQuotes = false;
      continue;
    }

    // Allow newlines inside quoted values
    if (char === "\n" && insideQuotes) {
      currentToken += "\n";
      continue;
    }
    if (char === "\r" && insideQuotes) {
      currentToken += "\r";
      continue;
    }

    // Value separator: comma outside quotes only
    if (char === "," && !insideQuotes && emittedCount < expectedKeysCount - 1) {
      values.push(currentToken.trim());
      currentToken = "";
      emittedCount++;
      continue;
    }

    currentToken += char;
  }

  // Push last value
  values.push(currentToken.trim());

  // Cleanup: strip outer quotes, unescape, normalize EOLs
  return values.map((raw) => {
    const isWrapped = raw.startsWith('"') && raw.endsWith('"');
    const withoutOuterQuotes = isWrapped ? raw.slice(1, -1) : raw;

    return withoutOuterQuotes
      .replace(/\\"/g, '"') // \" -> "
      .replace(/\r\n/g, "\n")
      .replace(/\r/g, "\n")
      .trim();
  });
};

/**
 * Parse a single log line ("author:...|edited:...").
 * Only the first '|' is used to split author|payload.
 */
const _parseSingleLogLine = (line: string, language: string): ParsedLog | null => {
  // Split author segment and payload using ONLY the first '|'
  const firstPipeIndex = line.indexOf("|");
  if (firstPipeIndex === -1) return null;

  const authorSegment = line.slice(0, firstPipeIndex);
  const payloadSegment = line.slice(firstPipeIndex + 1);

  const authorMatch = authorSegment.match(/^author:(\d+)/);
  if (!authorMatch) return null;
  const authorId = authorMatch[1];

  // Action (edited|added|deleted) is before the first ':' in payload
  const actionSepIndex = payloadSegment.indexOf(":");
  if (actionSepIndex === -1) return null;

  const action = payloadSegment.slice(0, actionSepIndex) as LogAction;
  if (!["edited", "added", "deleted"].includes(action)) return null;

  const remainderAfterAction = payloadSegment.slice(actionSepIndex + 1);

  // Split keysCsv and valuesCsv with the FIRST following ':'
  const keysSepIndex = remainderAfterAction.indexOf(":");
  const keysCsv = keysSepIndex === -1 ? remainderAfterAction : remainderAfterAction.slice(0, keysSepIndex);
  const valuesCsv = keysSepIndex === -1 ? "" : remainderAfterAction.slice(keysSepIndex + 1);

  const keys = keysCsv
    .split(",")
    .map((key) => key.trim())
    .filter(Boolean);

  // ----- Special cases when exactly one key is present -----
  if (keys.length === 1) {
    switch (keys[0]) {
      case "user_attendance": {
        // Expected format: "userId:true|false"
        const attendanceMatch = valuesCsv.match(/^\s*(\d+)\s*:\s*(true|false)\s*$/i);
        if (attendanceMatch) {
          const userId = attendanceMatch[1];
          const attended = attendanceMatch[2].toLowerCase() === "true";

          const statusText = attended
            ? translate(language, "PAGES.MEETING.LOGS.ATTENDANCE.STATUSES.PRESENT")
            : translate(language, "PAGES.MEETING.LOGS.ATTENDANCE.STATUSES.JUSTIFIED_ABSENCE");

          return {
            authorId,
            action,
            items: [{ key: "attendance_update", value: `${userId} → ${statusText}` }],
            raw: line,
          };
        }
        break;
      }

      case "attendance": {
        const cleanedValue = _splitCsvValuesForKeys(valuesCsv, 1)[0] ?? "";
        const token = cleanedValue.trim().toLowerCase();
        let valueText = cleanedValue;

        if (token === "locked") {
          valueText = translate(language, "PAGES.MEETING.LOGS.ATTENDANCE.VALUES.LOCKED");
        } else if (token === "link") {
          valueText = translate(language, "PAGES.MEETING.LOGS.ATTENDANCE.VALUES.LINK");
        }

        return {
          authorId,
          action,
          items: [{ key: "attendance", value: valueText }],
          raw: line,
        };
      }

      case "resource": {
        /**
         * Three possible formats after "resource:":
         * 1) <uuid>
         * 2) <uuid>:<keysCsv>
         * 3) <uuid>:<keysCsv>:<valuesCsv>
         */
        const resourcePayload = valuesCsv;
        const firstSep = resourcePayload.indexOf(":");

        // (1) Only the resource id
        if (firstSep === -1) {
          const resourceId = resourcePayload.trim();
          return {
            authorId,
            action,
            items: [{ key: "resource.id", value: resourceId }],
            raw: line,
          };
        }

        const resourceId = resourcePayload.slice(0, firstSep).trim();
        const resourceRemainder = resourcePayload.slice(firstSep + 1);

        const secondSep = resourceRemainder.indexOf(":");
        const resourceKeysCsv =
          secondSep === -1 ? resourceRemainder.trim() : resourceRemainder.slice(0, secondSep).trim();
        const resourceValuesCsv = secondSep === -1 ? "" : resourceRemainder.slice(secondSep + 1);

        const resourceKeys = resourceKeysCsv
          .split(",")
          .map((k) => k.trim())
          .filter(Boolean);

        // (2) ID + keys, no values
        if (!resourceValuesCsv) {
          return {
            authorId,
            action,
            items: [
              { key: "resource.id", value: resourceId },
              ...resourceKeys.map((keyName) => ({ key: `resource.${keyName}`, value: "" })),
            ],
            raw: line,
          };
        }

        // (3) ID + keys + values
        const resourceValues = _splitCsvValuesForKeys(resourceValuesCsv, resourceKeys.length);

        return {
          authorId,
          action,
          items: [
            { key: "resource.id", value: resourceId },
            ...resourceKeys.map((keyName, valueIndex) => ({
              key: `resource.${keyName}`,
              value: resourceValues[valueIndex] ?? "",
            })),
          ],
          raw: line,
        };
      }
    }
  }

  // ----- Generic case: N keys / N CSV values -----
  const values = _splitCsvValuesForKeys(valuesCsv, keys.length);
  const items: LogItem[] = keys.map((keyName, index) => ({
    key: keyName,
    value: values[index] ?? null,
  }));

  return { authorId, action, items, raw: line };
};

/**
 * Parse a raw block of logs.
 * 1) Group entries (multi-line)
 * 2) Parse each entry
 */
export const parseLogs = (rawText: string, language: string): ParsedLog[] =>
  splitLogEntries(rawText)
    .filter((entry) => entry.startsWith("author:"))
    .map((entry) => _parseSingleLogLine(entry, language))
    .filter((parsed): parsed is ParsedLog => !!parsed);

/**
 * Whether a key is a free-text field whose newlines should be preserved (UI hint).
 */
export const shouldPreserveNewlines = (key?: string | null): boolean => {
  if (!key) return false;
  return /(^|\.)((meeting_description|description|note|notes)|resource\.description)$/i.test(key);
};

/** Validate ISO 8601 datetime. */
export const isISODate = (input?: string | null): boolean => {
  if (!input) return false;
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(input);
};

/** Validate HTTP/HTTPS URL. */
export const isUrl = (input?: string | null): boolean => {
  if (!input) return false;
  return /^https?:\/\//i.test(input);
};

/** Map a log action to a CSS class for badges. */
export const badgeClass: Record<LogAction, string> = {
  edited: "bg-primary",
  added: "bg-success",
  deleted: "bg-danger",
};
